import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Download } from 'lucide-react';
import { useTheme, cn } from '@/ui/theme/ThemeContext';
import { useProjectData } from '@/features/projects/ProjectDataContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { log } from '@/shared/utils/logger';
import { convertTeamGanttData, TeamGanttImportError, type ImportStats } from '../utils/teamGanttConverter';
import type { ProjectData } from '@/shared/types/projectData';

interface TeamGanttImporterProps {
  onSuccess?: () => void;
  compactHeader?: boolean;
}

export const TeamGanttImporter: React.FC<TeamGanttImporterProps> = ({ onSuccess, compactHeader = false }) => {
  const { theme } = useTheme();
  const { t } = useSettings();
  const { importProjectData } = useProjectData();

  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    message: string;
    stats?: ImportStats;
    data?: ProjectData;
  } | null>(null);
  const mountedRef = useRef(true);
  const operationRef = useRef(0);
  const processingRef = useRef(false);
  const successTimerRef = useRef<number | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      operationRef.current += 1;
      if (successTimerRef.current !== null) window.clearTimeout(successTimerRef.current);
    };
  }, []);
  const secondaryButtonClass = cn(
    'flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors',
    theme.borderPrimary,
    theme.textSecondary,
    theme.hover
  );

  const handleFileSelect = useCallback(async (file: File) => {
    if (processingRef.current) return;
    processingRef.current = true;
    const operation = ++operationRef.current;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      if (mountedRef.current && operationRef.current === operation) setImportResult({
        success: false,
        message: t('Please select a CSV file exported from TeamGantt.')
      });
      processingRef.current = false;
      return;
    }

    setIsProcessing(true);
    setImportResult(null);

    try {
      const csvContent = await file.text();
      if (!mountedRef.current || operationRef.current !== operation) return;
      const { projectData, stats } = convertTeamGanttData(csvContent);

      log.info('TeamGantt Import', 'Processed Data:', {
        projectName: projectData.projectName,
        projectId: projectData.projectId,
        nodeCount: stats.totalNodes,
        stats
      });

      // Call the real import function to save to backend
      await importProjectData(projectData);

      // Show success message after successful import
      if (!mountedRef.current || operationRef.current !== operation) return;
      setImportResult({
        success: true,
        message: t('TeamGantt project "{{projectName}}" imported successfully! ({{count}} items)', { projectName: projectData.projectName, count: stats.totalNodes }),
        stats,
        data: projectData
      });

      // Call the success callback if provided (e.g., to close the modal)
      if (onSuccess) {
        // Delay slightly so user can see the success message
        successTimerRef.current = window.setTimeout(() => {
          if (mountedRef.current && operationRef.current === operation) onSuccess();
        }, 2000);
      }

    } catch (error) {
      log.error('TeamGantt Import', 'Import error:', error);
      if (mountedRef.current && operationRef.current === operation) setImportResult({
        success: false,
        // Validation errors carry their English source as the key; the CSV values are interpolated.
        message: error instanceof TeamGanttImportError
          ? t(error.messageKey, error.params)
          : error instanceof Error ? error.message : t('Failed to import TeamGantt CSV')
      });
    } finally {
      if (operationRef.current === operation) processingRef.current = false;
      if (mountedRef.current && operationRef.current === operation) setIsProcessing(false);
    }
  }, [importProjectData, onSuccess, t]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const downloadSample = useCallback(() => {
    // Create a sample CSV that matches TeamGantt format (headers only)
    const sampleCSV = `"WBS #","Name / Title",Type,"Start Date","End Date","Estimated Hours","Actual Hours","Percent Complete",Resources,Predecessors,Notes,Comments,"Task Color"`;

    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'teamgantt-sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <div className={cn('space-y-6', theme.bgPrimary)}>
      <div className="space-y-4">
        {compactHeader ? (
          <div className="flex justify-end">
            <button
              onClick={downloadSample}
              className={secondaryButtonClass}
            >
              <Download size={16} />
              {t('Sample CSV')}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <h2 className={cn('text-2xl font-bold', theme.textPrimary)}>
                {t('Import TeamGantt Project')}
              </h2>
              <p className={cn('text-sm mt-1', theme.textSecondary)}>
                {t('Import project structure, dates, progress, and work hours from TeamGantt CSV.')}
              </p>
            </div>
            <button
              onClick={downloadSample}
              className={secondaryButtonClass}
            >
              <Download size={16} />
              {t('Sample CSV')}
            </button>
          </div>
        )}

        <p className={cn('text-sm', theme.textSecondary)}>
          {t('Leave the Resources column empty. Add project members and assign tasks after importing.')}
        </p>

        {/* Drop Zone */}
        <div
          className={cn(
            'relative border-2 border-dashed rounded-lg p-8 text-center transition-colors',
            isDragging ? cn(theme.surfaceSelected, theme.selectionRing, 'border-transparent') : theme.borderPrimary,
            isProcessing && 'opacity-50 pointer-events-none'
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="space-y-4">
            <div className={cn(
              'mx-auto w-16 h-16 rounded-full flex items-center justify-center',
              isDragging ? cn(theme.surfaceSelected, theme.textPrimary) : cn(theme.bgSecondary, theme.textSecondary)
            )}>
              {isProcessing ? (
                <div className="animate-spin w-6 h-6 border-2 border-current border-t-transparent rounded-full" />
              ) : (
                <Upload size={24} />
              )}
            </div>
            
            {isProcessing ? (
              <div>
                <p className={cn('text-lg font-medium', theme.textPrimary)}>
                  {t('Processing TeamGantt CSV...')}
                </p>
                <p className={cn('text-sm', theme.textSecondary)}>
                  {t('Importing supported project fields...')}
                </p>
              </div>
            ) : (
              <div>
                <p className={cn('text-lg font-medium', theme.textPrimary)}>
                  {isDragging ? t('Drop your TeamGantt CSV here') : t('Drag and drop your TeamGantt CSV here')}
                </p>
                <p className={cn('text-sm mt-2', theme.textSecondary)}>
                  {t('or click to browse for files')}
                </p>
              </div>
            )}

            <input
              type="file"
              accept=".csv"
              aria-label={t('Import TeamGantt CSV')}
              onChange={handleFileInput}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={isProcessing}
            />
          </div>
        </div>

        {/* Import Result */}
        {importResult && (
          <div className={cn(
            'rounded-lg p-4 border',
            importResult.success ? theme.accentGreen : theme.accentRed
          )}>
            <div className="flex items-start gap-3">
              {importResult.success ? (
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
              ) : (
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              )}
              <div className="flex-1">
                <p className={cn(
                  'font-medium',
                  importResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-600 dark:text-red-300'
                )}>
                  {importResult.message}
                </p>
                
                {importResult.success && importResult.stats && (
                  <div className="mt-3 space-y-2">
                    <p className={cn('text-sm font-medium', 'text-green-700 dark:text-green-300')}>
                      {t('Import Summary:')}
                    </p>
                    <div className={cn('text-sm grid grid-cols-2 gap-2', 'text-green-700 dark:text-green-400')}>
                      <div>{t('• Total Items: {{count}}', { count: importResult.stats.totalNodes })}</div>
                      <div>{t('• Groups: {{count}}', { count: importResult.stats.groups })}</div>
                      <div>{t('• Tasks: {{count}}', { count: importResult.stats.tasks })}</div>
                      <div>{t('• Milestones: {{count}}', { count: importResult.stats.milestones })}</div>
                      {importResult.stats.withDependencies > 0 && (
                        <div className="col-span-2">{t('• With Dependencies: {{count}}', { count: importResult.stats.withDependencies })}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Information */}
        <div className={cn('rounded-lg p-4 border', theme.borderPrimary, theme.bgSecondary)}>
          <div className="flex items-start gap-3">
            <FileText className={cn('flex-shrink-0 mt-0.5', theme.textSecondary)} size={20} />
            <div className="space-y-2">
              <h3 className={cn('font-medium', theme.textPrimary)}>
                {t('How to Export from TeamGantt')}
              </h3>
              <ol className={cn('text-sm space-y-1 list-decimal list-inside', theme.textSecondary)}>
                <li>{t('Open your project in TeamGantt')}</li>
                <li>{t('Click on "Export" in the top menu')}</li>
                <li>{t('Select "CSV" format')}</li>
                <li>{t('Download the file and upload it here')}</li>
              </ol>
              <p className={cn('text-sm font-medium mt-3', theme.textPrimary)}>
                {t('✨ All data will be automatically preserved:')}
              </p>
              <ul className={cn('text-sm space-y-1 list-disc list-inside ml-4', theme.textSecondary)}>
                <li>{t('Project hierarchy and WBS structure')}</li>
                <li>{t('Tasks, groups, and milestones')}</li>
                <li>{t('Start/end dates and progress')}</li>
                <li>{t('Assignees and resources')}</li>
                <li>{t('Dependencies and predecessors')}</li>
                <li>{t('Notes, comments, and colors')}</li>
                <li>{t('Estimated and actual hours')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
